import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Client } from '../../types/client';
import { accountService, userService } from '../../services';
import { clientService } from '../../services/client/clientService';
import { NewAccountReviewRow } from './NewAccountReviewRow';
import { ExistingAccountReviewRow } from './ExistingAccountReviewRow';
import { UserOption } from './UserAssignmentRow';
import { useReviewModalAssignments, ExistingAssignmentMap } from './useReviewModalAssignments';
import { formatCurrency } from '../../utils/currencyUtils';

interface NewAccount {
  account_string: string;
  transaction_count: number;
  total_commission: number;
}

/**
 * An EXISTING account that matched a CSV row but currently has zero active
 * employees. Without intervention, 100% of its commission silently flows to
 * House. The modal surfaces these in a separate section so the user can
 * assign employees on the upload-time review screen.
 */
export interface MatchedAccountNeedingEmployees {
  account_id: number;
  account_name: string;
  client_name: string;
  client_id: number;
  account_string: string;
  has_employees: false;
}

export interface AccountAssignment {
  client_id: number | null;
  user_assignments: { user_id: number; commission_percentage: number }[];
}

/** Per-account payload for matched-but-employee-less accounts. */
export interface ExistingAccountAssignment {
  account_id: number;
  user_assignments: { user_id: number; commission_percentage: number }[];
}

/**
 * R15 Task B1: prior modal state carried across a 422 retry so the user
 * does not lose already-entered assignments. Parent captures this on
 * Confirm, then passes it back if the upload's 422 unresolved_accounts
 * response reopens the modal.
 */
export interface ReviewModalInitialState {
  newAssignments: Record<string, AccountAssignment>;
  existingAssignments: ExistingAssignmentMap;
  houseOverrides: number[];
}

interface NewAccountReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Three-argument callback so the parent can route each group to its own
   * downstream API: new accounts go through applyAssignments (which creates
   * accounts during processing), existing accounts go through
   * bulkAssignEmployees (which appends employees without recreating), and
   * House-override account ids are forwarded to the upload call's
   * `house_overrides[]` so the backend persists a House UserAccount before
   * processing begins (R13 Task C-r).
   */
  onProceed: (
    newAssignments: Record<string, AccountAssignment>,
    existingAssignments: ExistingAccountAssignment[],
    houseOverrideAccountIds: number[]
  ) => void;
  onSkip: () => void;
  newAccounts: NewAccount[];
  matchedAccountsNeedingEmployees?: MatchedAccountNeedingEmployees[];
  filename: string;
  totalTransactions: number;
  totalCommission: number;
  /**
   * R15 Task B1: if provided, seed the modal with these prior assignments
   * (used to restore state after a 422 retry re-opens the modal).
   */
  initialState?: ReviewModalInitialState | null;
}

export const NewAccountReviewModal: React.FC<NewAccountReviewModalProps> = ({
  isOpen,
  onClose,
  onProceed,
  onSkip,
  newAccounts,
  matchedAccountsNeedingEmployees = [],
  filename,
  totalTransactions,
  totalCommission,
  initialState = null,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [expandedNew, setExpandedNew] = useState<Set<string>>(new Set());
  const [expandedExisting, setExpandedExisting] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  // Inline "create new client" UX: tracks which row has the form open.
  const [showCreateClientFor, setShowCreateClientFor] = useState<string | null>(null);
  const [newClientName, setNewClientName] = useState<string>('');
  const [creatingClient, setCreatingClient] = useState<boolean>(false);
  // R13 Task C-r: account ids the user has explicitly opted into routing
  // 100% commission to House for. Forwarded to upload as house_overrides[].
  const [houseOverrides, setHouseOverrides] = useState<Set<number>>(new Set());
  // R15 Task B3: submit-lock prevents double-firing bulkAssignEmployees +
  // double-uploading if the Confirm button is double-clicked. React's
  // disabled prop alone doesn't cover the tiny window between click and
  // re-render — the early-return in handleProceed guards that.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const assignments = useReviewModalAssignments(
    newAccounts,
    matchedAccountsNeedingEmployees,
    initialState?.newAssignments,
    initialState?.existingAssignments
  );

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const [clientsRes, usersRes] = await Promise.all([
          accountService.getAvailableClients(),
          userService.getUsers({ per_page: 1000, is_active: true }),
        ]);
        setClients(clientsRes.data || []);
        setUsers(
          (usersRes.data || [])
            .map((u: any) => ({
              id: u.id,
              name: u.name,
              email: u.email,
              is_house: !!u.is_house,
            }))
            .sort((a: UserOption, b: UserOption) => {
              if (a.is_house !== b.is_house) return a.is_house ? -1 : 1;
              return a.name.localeCompare(b.name);
            })
        );
      } catch (err) {
        console.error('Failed to load clients/users', err);
      } finally {
        setLoading(false);
      }
    };

    setExpandedNew(new Set());
    setExpandedExisting(new Set());
    // R15 Task B1: seed houseOverrides from initialState (empty on first
    // open; carries forward the user's ticks on a 422 retry).
    setHouseOverrides(new Set(initialState?.houseOverrides ?? []));
    // R15 Task B3: release the submit-lock every time the modal opens so
    // the retry path (422 → reopen) starts with a fresh, clickable button.
    setIsSubmitting(false);
    assignments.reset();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, newAccounts, matchedAccountsNeedingEmployees, initialState]);

  const toggleExpandNew = (s: string) =>
    setExpandedNew((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });

  const toggleExpandExisting = (id: number) =>
    setExpandedExisting((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleCreateClient = async (accountString: string) => {
    const trimmed = newClientName.trim();
    if (!trimmed || creatingClient) return;
    setCreatingClient(true);
    try {
      const resp = await clientService.createClient({ client_name: trimmed });
      const created = resp.data as unknown as Client;
      if (!created?.id) throw new Error('Create succeeded but response missing client data');
      setClients((prev) => [...prev, created]);
      assignments.setClient(accountString, created.id);
      setShowCreateClientFor(null);
      setNewClientName('');
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.message || err?.message || 'Failed to create client');
    } finally {
      setCreatingClient(false);
    }
  };

  const toggleHouseOverride = (accountId: number) =>
    setHouseOverrides((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
        // Note: any half-typed employee rows are kept in local state but
        // filtered out at handleProceed time — see the cleanExisting filter
        // below. This avoids stale-index bugs from iterative removeUser calls
        // inside a setState updater.
      }
      return next;
    });

  const handleProceed = () => {
    // R15 Task B3: submit-lock — guard against double-click firing
    // bulkAssignEmployees + the upload twice. The button `disabled`
    // prop also reflects isSubmitting, but this early-return covers
    // the brief pre-render window.
    if (isSubmitting) return;
    setIsSubmitting(true);
    const { filteredNew, filteredExisting } = assignments.buildPayloads();
    // Drop any user_assignments for accounts the user explicitly sent to House
    // (defense in depth — the UI already gates this).
    const cleanExisting = filteredExisting.filter((a) => !houseOverrides.has(a.account_id));
    onProceed(filteredNew, cleanExisting, Array.from(houseOverrides));
  };

  if (!isOpen) return null;

  const newCount = newAccounts.length;
  const emptyCount = matchedAccountsNeedingEmployees.length;
  const hasNew = newCount > 0;
  const hasEmpty = emptyCount > 0;

  // R13 Task C-r: proceed is blocked until every empty account is resolved
  // (either real employees configured OR ticked Send to House). The skip path
  // is similarly gated — without this guard the user could bypass the whole
  // contract by clicking "Skip — upload without assigning".
  const unresolvedExisting = matchedAccountsNeedingEmployees.filter((acc) => {
    if (houseOverrides.has(acc.account_id)) return false;
    const list = assignments.existingAssignments[acc.account_id]?.user_assignments ?? [];
    return !list.some((ua) => ua.user_id > 0 && ua.commission_percentage > 0);
  });
  const proceedBlocked = unresolvedExisting.length > 0;

  const headerTitle = hasNew && hasEmpty
    ? 'Accounts Need Review'
    : hasEmpty
      ? 'Accounts Missing Employees'
      : 'New Accounts Found';

  const headerSubtitle = hasNew && hasEmpty
    ? `${newCount} new + ${emptyCount} missing employees in ${filename}`
    : hasEmpty
      ? `${emptyCount} existing account${emptyCount !== 1 ? 's' : ''} with no employees in ${filename}`
      : `${newCount} new account${newCount !== 1 ? 's' : ''} found in ${filename}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{headerTitle}</h3>
              <p className="text-sm text-gray-600 mt-0.5">{headerSubtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center justify-between text-sm">
          <span className="text-amber-800">
            {hasEmpty
              ? 'Assign employees to existing accounts so their commissions are not routed entirely to House.'
              : 'These accounts have no matching rules. Assign them to a client so commissions are calculated correctly.'}
          </span>
          <span className="text-amber-900 font-medium">
            {totalTransactions} trades &middot; {formatCurrency(totalCommission)}
          </span>
        </div>

        {/* R13 Task C-r: red blocker — visible while ANY existing-empty account
            is still unresolved. Disappears once every row is either assigned
            employees or ticked Send to House. */}
        {hasEmpty && proceedBlocked && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-3 text-sm text-red-800">
            <strong>{unresolvedExisting.length}</strong>{' '}
            account{unresolvedExisting.length !== 1 ? 's have' : ' has'} no employee assignments.
            Resolve each one (assign employees or tick &ldquo;Send to House&rdquo;) before uploading.
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading clients and employees...</div>
          ) : (
            <>
              {hasNew && (
                <section>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    New accounts needing assignment ({newCount})
                  </h4>
                  <div className="space-y-3">
                    {newAccounts.map((account) => (
                      <NewAccountReviewRow
                        key={account.account_string}
                        account={account}
                        assignment={assignments.newAssignments[account.account_string]}
                        isExpanded={expandedNew.has(account.account_string)}
                        clients={clients}
                        users={users}
                        showCreateClient={showCreateClientFor === account.account_string}
                        newClientName={newClientName}
                        creatingClient={creatingClient}
                        onToggleExpand={() => toggleExpandNew(account.account_string)}
                        onSetClient={(id) => assignments.setClient(account.account_string, id)}
                        onOpenCreateClient={() => {
                          setShowCreateClientFor(account.account_string);
                          setNewClientName('');
                        }}
                        onCloseCreateClient={() => {
                          setShowCreateClientFor(null);
                          setNewClientName('');
                        }}
                        onChangeNewClientName={setNewClientName}
                        onCreateClient={() => handleCreateClient(account.account_string)}
                        onAddUser={() => assignments.addNewUser(account.account_string)}
                        onUpdateUser={(idx, field, value) =>
                          assignments.updateNewUser(account.account_string, idx, field, value)
                        }
                        onRemoveUser={(idx) =>
                          assignments.removeNewUser(account.account_string, idx)
                        }
                      />
                    ))}
                  </div>
                </section>
              )}

              {hasEmpty && (
                <section>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Existing accounts missing employees ({emptyCount})
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">
                    These accounts already exist with a client assigned, but no employees are set
                    up. Without assigning employees, 100% of their commission will go to House.
                  </p>
                  <div className="space-y-3">
                    {matchedAccountsNeedingEmployees.map((account) => (
                      <ExistingAccountReviewRow
                        key={account.account_id}
                        account={account}
                        userAssignments={
                          assignments.existingAssignments[account.account_id]?.user_assignments ||
                          []
                        }
                        isExpanded={expandedExisting.has(account.account_id)}
                        users={users}
                        sendToHouse={houseOverrides.has(account.account_id)}
                        onToggleSendToHouse={() => toggleHouseOverride(account.account_id)}
                        onToggleExpand={() => toggleExpandExisting(account.account_id)}
                        onAddUser={() => assignments.addExistingUser(account.account_id)}
                        onUpdateUser={(idx, field, value) =>
                          assignments.updateExistingUser(account.account_id, idx, field, value)
                        }
                        onRemoveUser={(idx) =>
                          assignments.removeExistingUser(account.account_id, idx)
                        }
                      />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          {/* R13 Task C-r: the skip path is only allowed when there are no
              employee-less existing accounts. Once Peter rejected the silent
              House fallback, "skip" can't mean "ignore unassigned" anymore. */}
          {hasEmpty ? (
            <span className="text-xs text-gray-500">
              Skip is disabled while accounts still need resolution.
            </span>
          ) : (
            <button onClick={onSkip} className="text-sm text-gray-600 hover:text-gray-800 underline">
              Skip &mdash; upload without assigning
            </button>
          )}
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleProceed}
              disabled={proceedBlocked || isSubmitting}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && (
                <span
                  aria-hidden="true"
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                />
              )}
              {isSubmitting ? 'Uploading...' : 'Confirm Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
